import React, { useState, useEffect } from 'react';

// ✅ 1. เปลี่ยนจาก Import API มาเป็นการเรียกใช้ Context
import { usePortfolio } from '../context/PortfolioContext';
import { useMarketPrices } from '../context/MarketPriceContext';
import { isCryptoSymbol } from '../composables/fetchCrypto';
import { holdingQuoteSymbol } from '../utils/portfolioType';
import { formatCryptoUsd, num } from '../utils/cryptoDecimal';

const getStoredToggle = (key: string, defaultValue: boolean) => {
  const saved = localStorage.getItem(key);
  return saved !== null ? JSON.parse(saved) : defaultValue;
};

interface LossCalculatorProps {
  symbol: string;
  setModeLoss?: (mode: string) => void;
  customPrice: number | '';
  setCustomPrice: (price: number | '') => void;
  portfolioId: string;
  subPortId: string;
  avgBuyPrice: number | null;
}

const LossCalculator: React.FC<LossCalculatorProps> = ({ symbol, setModeLoss, customPrice, setCustomPrice, portfolioId, subPortId, avgBuyPrice }) => {
  const [investment, setInvestment] = useState<number>(0);
  const [shares, setShares] = useState<number>(0);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [showCustomChangeMessage, setShowCustomChangeMessage] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [portfolioData, setPortfolioData] = useState<{ quantity: number, avgBuyPrice: number } | null>(null);
  
  const [showPortfolioDetails, setShowPortfolioDetails] = useState<boolean>(() =>
    getStoredToggle('ui_showLossCalcPortfolioDetails', false)
  );

  // ✅ 2. ดึงข้อมูลพอร์ตและราคามาจาก Context
  const { userPortfolios } = usePortfolio();
  const { currentPrices, fetchPricesForSymbols } = useMarketPrices();

  // คริปโต: ราคาเหรียญถูกต่ำกว่า $1 ได้ / จำนวนเหรียญเป็นเศษได้มาก (แสดงผลเท่านั้น)
  const isCrypto = isCryptoSymbol(symbol);
  const unit = isCrypto ? 'เหรียญ' : 'หุ้น';
  const usd = (n: number) => (isCrypto ? formatCryptoUsd(n) : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

  useEffect(() => {
    localStorage.setItem('ui_showLossCalcPortfolioDetails', JSON.stringify(showPortfolioDetails));
  }, [showPortfolioDetails]);

  // ✅ 3. ค้นหาข้อมูลพอร์ตจาก Context (ไม่ต้องรอ API)
  useEffect(() => {
    if (!symbol || !portfolioId || !subPortId) {
      setPortfolioData(null);
      return;
    }

    try {
      const portfolio = userPortfolios.find((p: any) => (p.portfolioId || p.portId || p.id) === portfolioId);
      const subPort = portfolio?.subPorts?.find((sp: any) => sp.subPortId === subPortId);
      const holdings = subPort?.holdings || subPort?.stocks || [];
      const match = holdings.find((h: any) => holdingQuoteSymbol(h) === symbol);

      if (match) {
        // num(): คริปโตส่งเป็น string ทศนิยมยาว
        setPortfolioData({
          quantity: num(match.quantity),
          avgBuyPrice: num(match.avgEntryPrice || match.avgBuyPrice || 0) // รองรับ V2
        });
      } else {
        setPortfolioData(null);
      }
    } catch (error) {
      console.error("Error extracting portfolio from context:", error);
      setPortfolioData(null);
    }
  }, [symbol, portfolioId, subPortId, userPortfolios]);

  useEffect(() => {
    // เมื่อเปลี่ยนหุ้น ให้รีเซ็ตค่าเป็น 0 เสมอ
    setInvestment(0);
    setShares(0);
    setCustomPrice('');
    setShowCustomChangeMessage(false);
  }, [symbol]);

  // ✅ 4. สั่งให้ Context ดึงราคา (ถ้ายังไม่มี)
  useEffect(() => {
    if (symbol) {
      fetchPricesForSymbols([symbol]);
    }
  }, [symbol, fetchPricesForSymbols]);

  // ✅ 5. อัปเดตราคา currentPrice ทันทีเมื่อ Context มีการเปลี่ยนแปลง
  useEffect(() => {
    const livePrice = currentPrices[symbol];
    if (livePrice && livePrice > 0) {
      setCurrentPrice(livePrice);
      setShares(investment > 0 ? investment / livePrice : 0);
    }
  }, [currentPrices, symbol, investment]);


  const handleSetMode = () => {
    if (setModeLoss) {
      setModeLoss('damage-view');
    }
  };

  const handlePriceUpdate = (price: number) => {
    setCustomPrice(price);
  };

  const calculateLoss = (percentage: number) => {
    const targetPrice = currentPrice * (1 - percentage / 100);
    const loss = (currentPrice - targetPrice) * shares;
    return { targetPrice, loss };
  };

  const handleInvestmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      const parsedValue = value === '' ? 0 : parseFloat(value);
      setInvestment(parsedValue);
      setShares(parsedValue > 0 && currentPrice > 0 ? parsedValue / currentPrice : 0);
    }
  };

  const handleCustomPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setCustomPrice(value);
    } else {
      setCustomPrice('');
    }

    if (typingTimeout) clearTimeout(typingTimeout);

    const timeout = setTimeout(() => {
      setShowCustomChangeMessage(true);
    }, 2000);

    setTypingTimeout(timeout);
    setShowCustomChangeMessage(false);
  };

  const calculateCustomLoss = () => {
    if (customPrice === '' || customPrice >= currentPrice) return null;
    const loss = (currentPrice - customPrice) * shares;
    return { customPrice, loss };
  };

  const totalCostBasis = ((portfolioData?.quantity || 0) * (portfolioData?.avgBuyPrice || 0)) + investment;
  const totalShares = (portfolioData?.quantity || 0) + shares;

  const calculateScenario = (percentage: number) => {
    const targetPrice = currentPrice * (1 - percentage / 100);
    const remainingValue = totalShares * targetPrice;
    const lossFromCurrent = remainingValue - (totalShares * currentPrice);
    return { targetPrice, remainingValue, lossFromCurrent };
  };
  
  const sc5 = calculateScenario(5);
  const sc10 = calculateScenario(10);
  const sc20 = calculateScenario(20);

  const calculateTotalLoss = (percentage: number) => {
    const targetPrice = currentPrice * (1 - percentage / 100);
    const valueAtTarget = totalShares * targetPrice;
    const totalLossAmount = valueAtTarget - totalCostBasis;
    return { targetPrice, totalLossAmount };
  };

  const loss5 = calculateTotalLoss(5);
  const loss10 = calculateTotalLoss(10);
  const loss20 = calculateTotalLoss(20);

  const customLoss = calculateCustomLoss();

  const calculateChange = () => {
    if (customPrice === '' || customPrice >= currentPrice) return null;
    const changePercentage = ((currentPrice - customPrice) / currentPrice) * 100;
    const currentTotalValue = totalShares * currentPrice;
    const targetTotalValue = totalShares * customPrice;
    const lossFromCurrent = currentTotalValue - targetTotalValue;
    return { changePercentage, lossFromCurrent };
  };
  
  const customChange = calculateChange();

  const currentTotalValue = portfolioData ? portfolioData.quantity * currentPrice : 0;
  const totalCostBasisReal = portfolioData ? portfolioData.quantity * portfolioData.avgBuyPrice : 0;
  const plAmount = currentTotalValue - totalCostBasisReal;
  const plPercentage = totalCostBasisReal > 0 ? (plAmount / totalCostBasisReal) * 100 : 0;
  const isProfit = plAmount >= 0;

  return (
    <div className="px-4 text-white rounded-lg" style={{ backgroundColor: 'var(--component-bg-color)' }}>
      {/* ...
        (UI ส่วนนี้ ไม่มีการเปลี่ยนแปลงใดๆ ยังคงทำงานได้เหมือนเดิมทั้งหมดครับ) 
        ...
      */}
      <div className="">
        <label className="block mb-2 text-sm" style={{ color: 'var(--text-color)' }}>
          เงินลงทุนเริ่มต้น <span style={{ color: 'var(--yellow-text-color)' }}>(USD)</span>
        </label>
        <input
          type="number"
          className="w-full px-3 py-2 rounded"
          style={{
            backgroundColor: 'var(--bg-item)',
            color: 'var(--text-color)',
            border: '1px solid var(--button-bg-color)',
          }}
          value={investment.toString() || ""}
          onChange={handleInvestmentChange}
          placeholder="0"
        />
        <p className="text-sm text-end text-gray-400 mt-1">{unit}ที่ได้: {shares.toLocaleString(undefined, { maximumFractionDigits: isCrypto ? 8 : 4 })} {unit}</p>
      </div>

      <div className="mb-2">
        <h5 className="text-xs font-semibold" style={{ color: 'var(--text-color)' }}>
          SCENARIO: กำหนดช่วง...
        </h5>
        <ul className="space-y-2">
          {[
            { label: '-5%😟', subLabel: 'Stop Loss เร็ว', data: sc5, color: 'border-yellow-400', textColor: 'text-yellow-600' },
            { label: '-10%😩', subLabel: 'เริ่มเจ็บหนัก', data: sc10, color: 'border-orange-400', textColor: 'text-orange-600' },
            { label: '-20%😱', subLabel: 'วิกฤต/ตลาดหนี', data: sc20, color: 'border-red-500', textColor: 'text-red-600' }
          ].map((item, idx) => (
            <li key={idx} className={`flex justify-between items-center px-3 py-2 rounded-lg border-l-4 ${item.color}`}
              style={{ backgroundColor: 'var(--component-bg-color)', boxShadow: '4px 4px 6px 4px rgba(0, 0, 0, 0.4)' }}>
              
              <div className="flex flex-col w-[35%]">
                <span className={`font-extrabold text-sm ${item.textColor}`}>
                  {item.label} ({usd(item.data.targetPrice)})
                </span>
                <span className={`text-gray-500 text-[10px] uppercase font-semibold  ${item.textColor}`} >
                  {item.subLabel}
                </span>
              </div>

              <div className="flex flex-col items-center w-[35%] border-x border-gray-100/30" style={{ borderColor: 'var(--text-color)' }} >
                <span className={`text-[13px] font-bold text-gray-200 ${item.textColor}`} >
                  ${usd(item.data.remainingValue)}
                </span>
                <span className={`text-[11px] text-gray-500 uppercase text-center leading-tight ${item.textColor}`} >มูลค่าที่เหลือ<br /></span>
              </div>

              <div className="flex flex-col items-end w-[30%]">
                <span className={`font-extrabold text-sm  text-red-500`}>
                  -${usd(Math.abs(item.data.lossFromCurrent))}
                </span>
                <span className={`text-[11px] text-gray-500 uppercase ${item.textColor}`}>เงินที่หายไป</span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <label className="block mb-1 text-sm" style={{ color: 'var(--text-color)' }}>
            ราคา{unit}ที่ต้องการคำนวณ
          </label>
          {customChange && showCustomChangeMessage && (
            <p className="text-sm text-red-500 fade-in-animation">
              "คุณรับไหวไหม ถ้าเงินหายไปเท่านี้?"
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="number"
            className="flex-1 px-3 py-2 rounded"
            style={{
              backgroundColor: 'var(--bg-item)',
              color: 'var(--text-color)',
              border: '1px solid var(--button-bg-color)',
            }}
            value={customPrice !== '' ? parseFloat(customPrice.toString()) : ''}
            onChange={handleCustomPriceChange}
            placeholder="0"
          />
          <button
            className="px-4 py-2 rounded bg-green-500 text-white hover:bg-green-600"
            onClick={handleSetMode}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="25" height="25" fill="none">
              <circle cx="25" cy="25" r="7" fill="#ffffff" />
              <line x1="25" y1="5" x2="25" y2="45" stroke="#ffffff" strokeWidth="2" />
              <line x1="5" y1="25" x2="45" y2="25" stroke="#ffffff" strokeWidth="2" />
              <circle cx="25" cy="25" r="15" fill="none" stroke="#ffffff" strokeWidth="2" />
            </svg>
          </button>
          <button
            className="px-4 py-2 rounded bg-gray-500 text-white hover:bg-red-600"
            onClick={() => {
              setCustomPrice('');
              if (setModeLoss) {
                setModeLoss('default');
              }
            }}
          >
            Reset
          </button>
        </div>

        {customPrice !== '' && customPrice > currentPrice && (
          <p className="text-sm text-red-500 mt-1">
            ราคานี้ มากกว่าราคาปัจจุบันจ้า~
          </p>
        )}
        {customChange && (
          <div>
            <div className="mt-2 flex justify-between">
              <p className="text-sm text-red-500">
                การเปลี่ยนแปลง: <span className='font-bold text-base text-red-500'>
                  {customChange.changePercentage.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                </span>
              </p>
              <p className="text-sm" style={{ color: 'var(--text-color)' }}>
                ความเสียหาย: <span className='font-bold text-base text-red-500'>
                  ${usd(customChange.lossFromCurrent)}
                </span>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LossCalculator;